'use client';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { RichText } from '@/app/_components/core/rich-text';
import { useLabel } from '@/app/_contexts/labels-context';
import {
  setSessionCookie,
  getCookie,
  BANNER_COOKIE_NAME,
} from '@/app/_lib/cookie-helpers';
import { cn } from '@/app/_lib/utils';
import type { BannerComponentProps, BannerVariant } from './banner.types';

const ANIMATION_DURATION = 300;

const getBannerStyles = (variant?: BannerVariant): string => {
  switch (variant) {
    case 'warning':
      return 'bg-banner-warning text-banner-warning-fg dark:bg-banner-warning-dark dark:text-banner-warning-fg-dark';
    case 'alert':
      return 'bg-banner-alert text-banner-alert-fg dark:bg-banner-alert-dark dark:text-banner-alert-fg-dark';
    case 'default':
    default:
      return 'bg-banner-accent text-primary-foreground';
  }
};

export default function BannerComponent({
  bannerLayout,
  content,
  variant = 'default',
  initialClosed = false,
}: BannerComponentProps) {
  // SSR always renders the banner so the cached HTML is identical for all
  // visitors. Returning closers never see it: the head inline script hides
  // it via html.banner-closed before first paint; after hydration we sync
  // the state so the DOM node is removed as well.
  const [shouldRender, setShouldRender] = useState(!initialClosed);
  const [isAnimating, setIsAnimating] = useState(!initialClosed);
  const closeLabel = useLabel('close', 'Schließen');
  const bannerLabel = useLabel('banner', 'Hinweisbanner');

  // Post-hydration sync (see comment above): SSR always renders the banner for
  // a cacheable HTML, so returning visitors must drop the DOM node on the client
  // once mounted. Intentional, not a derived value.
  useEffect(() => {
    if (getCookie(BANNER_COOKIE_NAME)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional post-hydration cookie sync for ISR-cached HTML
      setShouldRender(false);
    }
  }, []);

  const handleClose = () => {
    // Fade-out Animation
    setIsAnimating(false);
    // Warte auf Animation, dann entfernen
    setTimeout(() => {
      setShouldRender(false);
      setSessionCookie(BANNER_COOKIE_NAME, 'true');
      // Keep the CSS guard in sync (see banner.token.css / layout head script)
      document.documentElement.classList.add('banner-closed');
    }, ANIMATION_DURATION);
  };

  if (
    !content ||
    !shouldRender ||
    (bannerLayout !== 'default' &&
      bannerLayout !== 'warning' &&
      bannerLayout !== 'alert')
  ) {
    return null;
  }

  const bannerStyles = getBannerStyles(variant);

  return (
    <section
      aria-label={bannerLabel}
      data-component="banner"
      data-variant={variant}
      className={cn('w-full', bannerStyles, 'relative transition-opacity duration-300', isAnimating ? 'opacity-100' : 'opacity-0')}
    >
      <div className="container mx-auto py-3">
        <div className="flex items-center justify-start md:justify-center">
          <div className="max-w-banner-content-max min-w-0 text-left md:text-center">
            <RichText
              classConfig={{
                copytext: 'text-sm lg:text-sm',
                link: 'text-inherit',
              }}
            >
              {content}
            </RichText>
          </div>
        </div>
      </div>

      <button
        onClick={handleClose}
        aria-label={closeLabel}
        className="absolute top-1/2 right-2 -translate-y-1/2 transition-opacity hover:opacity-70"
        data-track-id="banner-close"
      >
        <X className="size-5 cursor-pointer" />
      </button>
    </section>
  );
}

export { BannerComponent };
